import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  FacebookAuthProvider,
} from "firebase/auth";
import { getFirestore, collection, doc, getDoc, setDoc, Timestamp, query, where, onSnapshot } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getAnalytics } from "firebase/analytics";
import { FIREBASE_API } from "src/config";
import useLocales from "src/hooks/useLocales";
import { NotificationManager } from "react-notifications";
import cryptoRandomString from "crypto-random-string";
// ----------------------------------------------------------------------

export const firebaseApp = initializeApp(FIREBASE_API);

const AUTH = getAuth(firebaseApp);
export const FUNCTIONS = getFunctions(firebaseApp);

export const DB = getFirestore(firebaseApp);
const analytics = getAnalytics(firebaseApp);

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIALISE") {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: "firebase",
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { currentLang } = useLocales();
  const [currentSubscription, setCurrentSubscription] = useState("");

  const [langProfile, setLangProfile] = useState(null);
  const [profile, setProfile] = useState(null);
  var provider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();
  // const scopes = ["email", "profile", "https://www.googleapis.com/auth/calendar"];

  useEffect(
    () =>
      onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          const userRef = doc(DB, "users", user.uid);
          const profileRef = doc(DB, "users", user.uid, "profile", currentLang.value);

          const q = query(collection(DB, "users", user.uid, "subscriptions"), where("status", "in", ["trialing", "active"]));
          onSnapshot(q, (snapshot) => {
            if (snapshot.docs.length > 0) {
              const doc = snapshot.docs[0];
              setCurrentSubscription({ id: doc.id, ...doc.data() });
            }
          });

          const docSnap = await getDoc(userRef);
          const profileSnap = await getDoc(profileRef);
          if (docSnap.exists()) {
            setProfile(docSnap.data());
          }
          if (profileSnap.exists()) {
            setLangProfile(profileSnap.data());
          }

          dispatch({
            type: "INITIALISE",
            payload: { isAuthenticated: true, user },
          });
        } else {
          dispatch({
            type: "INITIALISE",
            payload: { isAuthenticated: false, user: null },
          });
        }
      }),
    [dispatch]
  );

  const login = (email, password) => signInWithEmailAndPassword(AUTH, email, password);

  const register = (data) => {
    const { email, password, firstName, lastName, role, userName } = data;
    createUserWithEmailAndPassword(AUTH, email, password)
      .then(async (res) => {
        const userRef = doc(collection(DB, "users"), res.user?.uid);
        const userProfile = doc(DB, "users", res.user?.uid, "profiles", "en");
        const betterProEmail = cryptoRandomString({ length: 10 });
        await setDoc(userRef, {
          uid: res.user?.uid,
          email,
          role,
          userName,
          betterProEmail: `${betterProEmail}@mail.betterpro.ca`,
          displayName: `${firstName} ${lastName}`,
        });
        if (role === "consultant") {
          await setDoc(userProfile, { firstName, lastName }, { merge: true });
        }
      })
      .catch((error) => {
        return NotificationManager.error(error.code);
      });
  };

  const googleSignIn = ({ role }) => {
    provider.addScope("https://www.googleapis.com/auth/calendar	");
    provider.addScope("https://www.googleapis.com/auth/drive");
    provider.addScope("https://mail.google.com");

    signInWithPopup(AUTH, provider)
      .then(async (result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);

        const token = credential.accessToken;
        // The signed-in user info.

        await setDoc(doc(DB, "users", result.user?.uid), { role, displayName: result.user.displayName, token }, { merge: true });

        // ...
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        // The email of the user's account used.
        // const email = error.customData.email;
        // The AuthCredential type that was used.
        const credential = GoogleAuthProvider.credentialFromError(error);
        // ...
      });
  };

  const facebookSignIn = ({ role }) => {
    signInWithPopup(AUTH, facebookProvider)
      .then(async (result) => {
        console.log("result", result);
        await setDoc(doc(DB, "users", result.user?.uid), { role, displayName: result.user.displayName }, { merge: true });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const registerAssessment = (email, password, values, id) =>
    createUserWithEmailAndPassword(AUTH, email, password)
      .then(async (res) => {
        const userRef = doc(collection(DB, "users"), res.user?.uid);

        await setDoc(
          userRef,
          {
            uid: res.user?.uid,
            email,
            role: "applicant",
            displayName: `${values.firstName} ${values.lastName}`,
            ...values,
          },
          { merge: true }
        );
        await setDoc(
          doc(DB, "users", id),
          { assess: [{ date: Timestamp.now(), id: res.user?.uid }] },
          {
            merge: true,
          }
        );
      })
      .catch((error) => {
        return NotificationManager.error(error.code);
      });

  const resetEmail = (email) => {
    sendPasswordResetEmail(AUTH, email)
      .then(() => console.log("success"))
      .catch((error) => {
        console.log(error);
      });
  };
  const logout = () => signOut(AUTH);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "firebase",
        user: {
          id: state?.user?.uid,
          email: state?.user?.email,
          photoURL: state?.user?.photoURL || profile?.photoURL,
          displayName: state?.user?.displayName || profile?.displayName,
          role: state?.user?.role || profile?.role,
          userName: state?.user?.userName || profile?.userName,
          phoneNumber: state?.user?.phoneNumber || profile?.phoneNumber || "",
          country: profile?.country || "",
          address: profile?.address || "",
          state: profile?.state || "",
          city: profile?.city || "",
          zipCode: profile?.zipCode || "",
          licenseNumber: profile?.licenseNumber || "",
          betterProEmail: profile?.betterProEmail || "",
          token: profile?.token || "",

          companyName: profile?.companyName || "",
          companyEmail: profile?.companyEmail || "",
          companyLogoURL: profile?.companyLogoURL || "",
          companyPhoneNumber: profile?.companyPhoneNumber || "",
          companyCountry: profile?.companyCountry || "",
          companyAddress: profile?.companyAddress || "",
          companyState: profile?.companyState || "",
          companyCity: profile?.companyCity || "",
          companyDomain: profile?.companyDomain || "",
          smtpEmails: profile?.smtpEmails || [{ email: "" }],

          about: profile?.about || "",
          access: profile?.access || [],
          assess: profile?.assess || [],
          isPublic: profile?.isPublic || false,
        },
        profile,
        langProfile,
        currentSubscription,
        login,
        register,
        registerAssessment,
        logout,
        resetEmail,
        googleSignIn,
        facebookSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
